import React, { useEffect, useRef, useState } from 'react';
import '../assets/css/systemManagement.css';
import { ProjectsModel } from '../models/ProjectsModel';
import {
    FilterDataModel,
    SortModel,
    SortDirection,
    ActivityStatus,
    ProjectModel,
    BranchModel,
    UserModel,
} from '../models/superbit-api-models';
import Pagger from '../components/_Pagger';
import DeleteAlert from '../components/DeleteAlert';
import NewProject from './_NewProject';
import AddProjectUsers from './_AddProjectUsers';

const PAGE_SIZE = 50; // server-side page size (Constants.NUMBER_OF_OBJECTS_PER_PAGE)
const INDENT_PX = 22; // per-depth indentation of the name cell

// Sortable columns — `sortBy` matches the property names the old MVC view sent
// (data-sort-by) so the existing server-side sorting keeps working.
type Column = { key: string; label: string; sortBy?: string };
const COLUMNS: Column[] = [
    { key: 'name', label: 'שם', sortBy: 'Name' },
    { key: 'manager', label: 'מנהל', sortBy: 'ProjectManagerId' },
    { key: 'status', label: 'סטטוס', sortBy: 'Status' },
];

const isActive = (s?: ActivityStatus) => s === ActivityStatus._0; // 0 = Active (פעיל)

const ProjectsManagement = () => {
    const [projectsModel] = useState<ProjectsModel>(() => new ProjectsModel());

    const [projects, setProjects] = useState<ProjectModel[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    const [searchTerm, setSearchTerm] = useState<string>('');
    const [sortProp, setSortProp] = useState<string | undefined>(undefined);
    const [sortDir, setSortDir] = useState<SortDirection>(SortDirection._0);

    const [page, setPage] = useState<number>(1);
    const [totalItems, setTotalItems] = useState<number>(0);
    const [reloadTick, setReloadTick] = useState<number>(0); // bumped to force a reload (e.g. on refresh/clear)

    // Expanded row ids (projects and nested branches), collapsed by default like
    // the old plus/minus tree.
    const [expanded, setExpanded] = useState<Set<string>>(new Set());
    const [openMenuId, setOpenMenuId] = useState<string | undefined>(undefined);
    const [deleteId, setDeleteId] = useState<string | undefined>(undefined);

    // Add/edit modal. editProject set = edit; otherwise add.
    const [showModal, setShowModal] = useState<boolean>(false);
    const [editProject, setEditProject] = useState<ProjectModel | undefined>(undefined);

    // "Add users" tree modal.
    const [showAddUsers, setShowAddUsers] = useState<boolean>(false);
    const [addUsersProject, setAddUsersProject] = useState<ProjectModel | undefined>(undefined);

    const buildFilter = (): FilterDataModel => {
        const filter = new FilterDataModel();
        filter.searchTerm = searchTerm ? searchTerm : undefined;
        filter.page = page;
        if (sortProp) {
            const sort = new SortModel();
            sort.property = sortProp;
            sort.sortDirection = sortDir;
            filter.sortBy = [sort];
        }
        return filter;
    };

    const load = async () => {
        setLoading(true);
        try {
            const result = await projectsModel.projectSort(buildFilter());
            const list = result && result.projects ? result.projects : [];
            setProjects(list);
            setTotalItems(result && result.allRowsCounter ? result.allRowsCounter : 0);
            // When searching, auto-expand projects so matching branches/users are
            // visible (the server already returned the filtered sub-tree).
            if (searchTerm) {
                setExpanded(new Set(list.map((p) => p.id).filter((id): id is string => !!id)));
            }
        } catch (e) {
            console.error('Failed to load projects', e);
            setProjects([]);
            setTotalItems(0);
        } finally {
            setLoading(false);
        }
    };

    // Reload whenever sort or page change (reloadTick forces a reload).
    useEffect(() => {
        load();
    }, [sortProp, sortDir, page, reloadTick]);

    const onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') applySearch();
    };

    const clearFilters = () => {
        setSearchTerm('');
        setPage(1);
        setReloadTick((t) => t + 1);
    };

    const applySearch = () => {
        if (page !== 1) setPage(1); // page change triggers reload
        else load();
    };

    // Auto-search: reload shortly after the user stops typing (skip the initial mount).
    const searchDidMount = useRef(false);
    useEffect(() => {
        if (!searchDidMount.current) {
            searchDidMount.current = true;
            return;
        }
        const t = setTimeout(() => applySearch(), 400);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm]);

    const onSort = (column: Column) => {
        if (!column.sortBy) return;
        if (sortProp === column.sortBy) {
            setSortDir(sortDir === SortDirection._0 ? SortDirection._1 : SortDirection._0);
        } else {
            setSortProp(column.sortBy);
            setSortDir(SortDirection._0);
        }
    };

    const toggleExpand = (id?: string) => {
        if (!id) return;
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const toggleMenu = (id?: string) => {
        if (!id) return;
        setOpenMenuId(openMenuId === id ? undefined : id);
    };

    const onAddProject = () => {
        setEditProject(undefined);
        setShowModal(true);
    };

    const onEdit = (project: ProjectModel) => {
        setOpenMenuId(undefined);
        setEditProject(project);
        setShowModal(true);
    };

    const onAddUsers = (project: ProjectModel) => {
        setOpenMenuId(undefined);
        setAddUsersProject(project);
        setShowAddUsers(true);
    };

    const confirmDelete = async () => {
        if (!deleteId) return;
        try {
            await projectsModel.projectDelete(deleteId);
            await load();
        } catch (e) {
            console.error('Failed to delete project', e);
        } finally {
            setDeleteId(undefined);
        }
    };

    // ----- Branch sub-tree (a project's usersBranches) ------------------------
    // Branches belonging to a project carry parentId relationships and their own
    // usersInBranch, exactly like the standalone Branches tab. We group them by
    // parentId (roots = branches whose parent is not part of this project's set)
    // and render recursively, mirroring the old _BranchItems partial.
    const renderProjectBranches = (project: ProjectModel, depth: number): React.ReactNode[] => {
        const branches = project.usersBranches || [];
        if (branches.length === 0) return [];

        // Scope keys and expand-state to this project: the same branch can belong
        // to more than one project, so plain branch ids would collide in the
        // single flat <tbody> and toggle in both projects at once.
        const scope = project.id || '';

        const ids = new Set(branches.map((b) => b.id).filter((id): id is string => !!id));
        const childrenByParent = new Map<string, BranchModel[]>();
        branches.forEach((b) => {
            // A branch whose parent isn't in this project's set is treated as a root.
            const key = b.parentId && ids.has(b.parentId) ? b.parentId : '';
            if (!childrenByParent.has(key)) childrenByParent.set(key, []);
            childrenByParent.get(key)!.push(b);
        });

        const renderLevel = (parentId: string, lvl: number): React.ReactNode[] => {
            const rows: React.ReactNode[] = [];
            (childrenByParent.get(parentId) || []).forEach((branch) => {
                const childBranches = (branch.id && childrenByParent.get(branch.id)) || [];
                const branchUsers = branch.usersInBranch || [];
                const expandable = childBranches.length > 0 || branchUsers.length > 0;
                const expandId = `${scope}/${branch.id}`;
                const isOpen = !!branch.id && expanded.has(expandId);

                rows.push(
                    <tr key={`b_${scope}_${branch.id}`} className="branch-row">
                        <td className="bold branch-name-cell">
                            <span style={{ paddingRight: lvl * INDENT_PX }} className="branch-indent" />
                            {expandable ? (
                                <span
                                    className={`branch-toggle pointer ${isOpen ? 'open' : ''}`}
                                    onClick={() => toggleExpand(expandId)}
                                />
                            ) : (
                                <span className="branch-toggle branch-toggle--leaf" />
                            )}
                            <span>{branch.name}</span>
                        </td>
                        <td>{branch.branchManager ? branch.branchManager.fullName : ''}</td>
                        <td className={isActive(branch.status) ? 'online' : 'offline'}>
                            <span>{isActive(branch.status) ? 'פעיל' : 'לא פעיל'}</span>
                            <span className="dot" />
                        </td>
                        <td />
                    </tr>
                );

                if (expandable && isOpen) {
                    if (branch.id) rows.push(...renderLevel(branch.id, lvl + 1));
                    branchUsers.forEach((user) =>
                        rows.push(renderUserRow(user, lvl + 1, `${scope}_${branch.id}`))
                    );
                }
            });
            return rows;
        };

        return renderLevel('', depth);
    };

    const renderUserRow = (user: UserModel, depth: number, keyPrefix: string): React.ReactNode => (
        <tr key={`u_${keyPrefix}_${user.id}`} className="branch-user-row">
            <td className="branch-name-cell">
                <span style={{ paddingRight: depth * INDENT_PX }} className="branch-indent" />
                <span className="branch-toggle branch-toggle--leaf" />
                <span className="branch-user-icon" />
                <span>{user.fullName}</span>
            </td>
            <td />
            <td className={isActive(user.status) ? 'online' : 'offline'}>
                <span>{isActive(user.status) ? 'פעיל' : 'לא פעיל'}</span>
                <span className="dot" />
            </td>
            <td />
        </tr>
    );

    // ----- Project rows -------------------------------------------------------
    const renderProjectRows = (): React.ReactNode[] => {
        const rows: React.ReactNode[] = [];

        projects.forEach((project) => {
            const branches = project.usersBranches || [];
            const projectUsers = project.usersInProject || [];
            const expandable = branches.length > 0 || projectUsers.length > 0;
            const isOpen = !!project.id && expanded.has(project.id);

            rows.push(
                <tr key={project.id} className="branch-row">
                    <td className="bold branch-name-cell">
                        {expandable ? (
                            <span
                                className={`branch-toggle pointer ${isOpen ? 'open' : ''}`}
                                onClick={() => toggleExpand(project.id)}
                            />
                        ) : (
                            <span className="branch-toggle branch-toggle--leaf" />
                        )}
                        <span>{project.name}</span>
                    </td>
                    <td>{project.projectManager ? project.projectManager.fullName : ''}</td>
                    <td className={isActive(project.status) ? 'online' : 'offline'}>
                        <span>{isActive(project.status) ? 'פעיל' : 'לא פעיל'}</span>
                        <span className="dot" />
                    </td>
                    <td className="users-actions">
                        <button
                            type="button"
                            className="actionBtn pointer"
                            onClick={() => toggleMenu(project.id)}
                        >
                            <div className="edit-icon" />
                            <span>פעולות</span>
                            <div className="dropdown-icon" />
                        </button>
                        {openMenuId === project.id && (
                            <ul className="actionBtnMenu">
                                <li className="actionBtnMenuItem" onClick={() => onEdit(project)}>
                                    ערוך
                                </li>
                                <li className="actionBtnMenuItem" onClick={() => onAddUsers(project)}>
                                    הוסף משתמשים
                                </li>
                                <li
                                    className="actionBtnMenuItem"
                                    onClick={() => {
                                        setOpenMenuId(undefined);
                                        setDeleteId(project.id);
                                    }}
                                >
                                    מחק
                                </li>
                            </ul>
                        )}
                    </td>
                </tr>
            );

            if (expandable && isOpen) {
                rows.push(...renderProjectBranches(project, 1));
                projectUsers.forEach((user) => rows.push(renderUserRow(user, 1, `p_${project.id}`)));
            }
        });

        return rows;
    };

    const pagerConf = {
        pageSize: PAGE_SIZE,
        currentPage: page,
        totalItems: totalItems,
    };

    return (
        <div className="users-management rtl">
            <div className="users-toolbar flex-centered">
                <div className="users-search">
                    <span className="search-icon" />
                    <input
                        type="text"
                        className="users-search__input"
                        placeholder="חיפוש"
                        autoComplete="off"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={onSearchKeyDown}
                    />
                </div>
                <div className="users-refresh pointer" title="רענון" onClick={() => clearFilters()} />
            </div>

            <div className="users-table-wrapper">
                <table className="users-table">
                    <thead>
                        <tr>
                            {COLUMNS.map((col) => (
                                <th
                                    key={col.key}
                                    className={`users-th ${col.sortBy ? 'sortable pointer' : ''} ${
                                        sortProp === col.sortBy ? 'sorted' : ''
                                    }`}
                                    onClick={() => onSort(col)}
                                >
                                    <span>{col.label}</span>
                                    {col.sortBy && (
                                        <span
                                            className={`arrow-updown ${
                                                sortProp === col.sortBy
                                                    ? sortDir === SortDirection._0
                                                        ? 'asc'
                                                        : 'desc'
                                                    : ''
                                            }`}
                                        />
                                    )}
                                </th>
                            ))}
                            <th className="users-th users-th--add">
                                <div className="add-btn pointer" onClick={onAddProject}>
                                    <span className="add-file" />
                                    <span className="text">הוסף פרויקט</span>
                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {renderProjectRows()}
                        {!loading && projects.length === 0 && (
                            <tr>
                                <td className="users-empty" colSpan={COLUMNS.length + 1}>
                                    לא נמצאו פרויקטים
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {totalItems > 0 && (
                <div className="users-pager">
                    <Pagger pagerConf={pagerConf} changePage={(p: number) => setPage(p)} refresh={() => load()} />
                </div>
            )}

            <DeleteAlert
                show={!!deleteId}
                onClose={() => setDeleteId(undefined)}
                onConfirm={confirmDelete}
            />

            <NewProject
                show={showModal}
                project={editProject}
                onClose={() => setShowModal(false)}
                onSaved={() => {
                    setShowModal(false);
                    load();
                }}
            />

            <AddProjectUsers
                show={showAddUsers}
                project={addUsersProject}
                onClose={() => setShowAddUsers(false)}
                onSaved={() => {
                    setShowAddUsers(false);
                    load();
                }}
            />
        </div>
    );
};

export default ProjectsManagement;
export {};
